import {
  selectSelectedDate,
  selectTimeslots,
} from '../../../store/slices/selectors';
import {
  Stack,
  useViewport,
  Text,
  Inline,
  useDevice,
  useRequestStatus,
} from '@treatwell/ui';
import {httpGetJson} from '../../../api/http';
import {useState, useEffect} from 'react';
import {useAppSelector} from '../../../hooks/store';
import {selectChannelData} from '../../../store/slices/channel/slice';
import {
  selectVenueData,
  selectVenueId,
} from '../../../store/slices/venue/slice';
import {getVenueTreatmentsAndLocation} from '../../../utils/getVenueTreatmentsAndLocation';
import {generateBrowsePath} from '../../../utils/generateBrowsePath';
import {BrowsePageOutput, VenueListingResultOutput} from '../VenueCard/types';
import {VenueCard} from '../VenueCard/VenueCard';
import {AvailabilityApiResponseChannel} from '../../../../server/routes/availability/types';
import styles from './VenueCardList.module.css';
import clsx from 'clsx';
import {formatDate} from '../../../../shared/utils/date';
import {SDKEventAttrs} from '@mparticle/web-sdk';
import {countDaysFromCurrentDate} from '../../../utils/countDaysFromCurrentDate';
import {VenueCardSkeleton} from '../VenueCardSkeleton';
import {Trans, useTranslation} from 'react-i18next';
import {FindOther, NoResults} from '../SalonSuggestionLink';
import {TrackOnView} from '../../Tracking/TrackOnView';
import {AVAILABLE_SALONS_TRIGGER_CONTEXT} from '../../../utils/mParticleTracking';

export const VenueCardListContainer = () => {
  const selectedDate = useAppSelector(selectSelectedDate);
  const timeSlots = useAppSelector(selectTimeslots);
  const noAvailableTimeslots = timeSlots.length === 0;
  const venueId = useAppSelector(selectVenueId);
  let {
    type: {pluralName: venueType},
  } = useAppSelector(selectVenueData);
  const channelData = useAppSelector(selectChannelData);
  const lang = channelData.locale.split('_')[0];
  if (lang !== 'de') {
    venueType = venueType.toLowerCase();
  }
  const venueData = getVenueTreatmentsAndLocation(venueId);
  const formattedSelectedDate = formatDate(selectedDate, 'ddd, Do MMM');
  const venueLocationPoints = {
    lat: venueData?.locationPoint?.lat,
    lon: venueData?.locationPoint?.lon,
  };
  const browsePath = generateBrowsePath({
    selectedDate,
    latitude: venueData?.locationPoint?.lat,
    longitude: venueData?.locationPoint?.lon,
    radius: 10,
    treatmentCategoryIds: venueData?.treatmentCategoryIds,
  });
  const {t} = useTranslation();

  const isMobile = useViewport({device: ['mobile', 'tablet']});
  const {isMobile: isMobileDevice} = useDevice();
  const [browseResults, setBrowseResults] =
    useState<VenueListingResultOutput[]>();
  const {
    setStatusPending,
    setStatusFulfilled,
    setStatusRejected,
    isStatusPending,
    isStatusFulfilled,
  } = useRequestStatus();

  const showingAvailableSalons =
    isStatusFulfilled && browseResults && browseResults.length !== 0;

  useEffect(() => {
    if (noAvailableTimeslots) {
      setStatusPending();
      httpGetJson<BrowsePageOutput>({
        path: browsePath,
      })
        .then((response) => {
          setBrowseResults(response.results);
          setStatusFulfilled();
        })
        .catch(() => {
          setStatusRejected();
        });
    }

    return () => {
      setBrowseResults(undefined);
    };
  }, [
    browsePath,
    noAvailableTimeslots,
    setStatusFulfilled,
    setStatusPending,
    setStatusRejected,
  ]);

  if (!noAvailableTimeslots) return;

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      data-cy="VenueCardListContainer"
      className={clsx({
        [styles.containerNoResults]:
          !isStatusPending && !showingAvailableSalons,
      })}
    >
      {isStatusPending ? (
        <SkeletonLayout isMobile={isMobile} />
      ) : showingAvailableSalons ? (
        <Stack space="base">
          <Inline
            className={styles.containerTitle}
            align="start"
            justify="between"
          >
            <Stack space="md">
              <Text type="mdHeader">
                {t('salonSuggestions.title', {
                  venueType,
                  date: formattedSelectedDate,
                })}
              </Text>
              <Text type="caption" className={styles.subtitle}>
                <Trans i18nKey="salonSuggestions.subtitle">
                  Based on your selected treatments
                </Trans>
              </Text>
            </Stack>
            {!isMobile && (
              <div className={styles.linkContainer}>
                <FindOther />
              </div>
            )}
          </Inline>
          <VenueCardListLayout
            browseResults={browseResults}
            channelData={channelData}
            isMobile={isMobile}
            venueLocationPoints={venueLocationPoints}
            currentVenueId={venueId}
            isMobileDevice={isMobileDevice}
          />
          {isMobile && (
            <div className={styles.linkContainer}>
              <FindOther />
            </div>
          )}
        </Stack>
      ) : (
        <TrackOnView
          eventName="available_salon_viewed"
          eventAttributes={{
            country: channelData.country.countryCode,
            platform: 'web',
            is_web_mobile: isMobileDevice,
            trigger_context: AVAILABLE_SALONS_TRIGGER_CONTEXT,
            days_from_current_date: countDaysFromCurrentDate(),
            available_salon_count: 0,
          }}
        >
          <NoResults />
        </TrackOnView>
      )}
    </div>
  );
};

const VenueCardListLayout = ({
  browseResults,
  channelData,
  isMobile,
  isMobileDevice,
  venueLocationPoints,
  currentVenueId,
}: {
  browseResults: VenueListingResultOutput[];
  channelData: AvailabilityApiResponseChannel;
  isMobile: boolean;
  isMobileDevice: boolean;
  venueLocationPoints: {
    lat?: number;
    lon?: number;
  };
  currentVenueId?: number | null;
}) => {
  const filteredResults = browseResults.filter(
    (venue) => venue.data.id !== currentVenueId,
  );
  const venues = isMobile ? filteredResults : filteredResults.slice(0, 8);
  const daysFromCurrentDate = countDaysFromCurrentDate();

  if (venues.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx(styles.container, {
        'is-mobile': isMobile,
        'is-desktop': !isMobile,
      })}
    >
      {venues.map((venue, index) => {
        const eventAttributes: SDKEventAttrs = {
          country: channelData.country.countryCode,
          platform: 'web',
          is_web_mobile: isMobileDevice,
          trigger_context: AVAILABLE_SALONS_TRIGGER_CONTEXT,
          days_from_current_date: daysFromCurrentDate,
          card_position: index + 1,
          venue_rating: venue.data.rating.average,
          venue_review_count: venue.data.rating.count,
        };

        const cardProps = {
          ...venue.data,
          channelData,
          venueLocationPoints,
          eventAttributes,
        };

        if (index === 0) {
          return (
            <TrackOnView
              key={venue.data.id}
              eventName="available_salon_viewed"
              eventAttributes={{
                country: channelData.country.countryCode,
                platform: 'web',
                is_web_mobile: isMobileDevice,
                trigger_context: AVAILABLE_SALONS_TRIGGER_CONTEXT,
                days_from_current_date: daysFromCurrentDate,
                available_salon_count: venues.length,
              }}
            >
              <VenueCard {...cardProps} />
            </TrackOnView>
          );
        }
        return <VenueCard {...cardProps} key={venue.data.id} />;
      })}
    </div>
  );
};

const SkeletonLayout = ({isMobile}: {isMobile: boolean}) => {
  return (
    <Stack space="md">
      <Text type="mdHeader" as="div" className={styles.containerTitle}>
        <Trans i18nKey="salonSuggestions.loading">
          Looking for nearby venues
        </Trans>
      </Text>
      <div
        className={clsx(styles.container, {
          'is-mobile': isMobile,
          'is-desktop': !isMobile,
        })}
      >
        {Array.from({length: 4}, (_, index) => (
          <VenueCardSkeleton key={index} />
        ))}
      </div>
    </Stack>
  );
};
